/**
 * アプリケーション固有のJavaScript
 *
 * このファイルに独自のプログラムを記述してください。
 * ベンダーライブラリとは分離されているため、自由に編集できます。
 *
 * 機能が増えてきた場合は modules/ フォルダに分割することを推奨します。
 * 例: import { initForm } from "./modules/form.js";
 */

// ========================================
// 定数
// ========================================

// 設定値
const HEADER_OFFSET = 80; // ヘッダー固定時の追加オフセット

// クラス名（状態）
const CLASS = {
  active: "is-active",
  scrolled: "is-scrolled",
  show: "is-show",
  blur: "is-blur",
};

// セレクター（HTML側と連動）
const SELECTOR = {
  header: "header",
  jsHeader: ".js-header",
  kv: "#kv",
  loading: ".js-loading",
  menu: ".js-menu",
  menuButton: ".js-menuButton",
  menuDropdown: ".js-menuDropdown",
  scrollAnimation: ".js-scrollAnimation",
  anchorLink: 'a[href^="#"]',
};

// ========================================
// ユーティリティ関数
// ========================================

/**
 * ヘッダーの高さを取得（オフセット込み）
 * @returns {number} ヘッダー高さ + オフセット
 */
function getHeaderHeight() {
  // const header = document.querySelector(SELECTOR.header);
  // return header ? header.offsetHeight + HEADER_OFFSET : HEADER_OFFSET;
  return HEADER_OFFSET;
}

/**
 * 指定位置へスクロール
 * @param {number} position - スクロール位置
 * @param {string} behavior - スクロール動作 ('smooth' | 'auto')
 */
function scrollToPosition(position, behavior = "smooth") {
  window.scroll({
    top: Math.max(0, position),
    behavior,
  });
}

// ========================================
// 機能別初期化関数
// ========================================

/**
 * ハンバーガーメニューの初期化
 */
function initHamburgerMenu() {
  const menu = document.querySelector(SELECTOR.menu);
  const menuButton = document.querySelector(SELECTOR.menuButton);
  const header = document.querySelector(SELECTOR.jsHeader);

  if (!menu || !menuButton) return null;

  const toggle = () => {
    menu.classList.toggle(CLASS.active);
    menuButton.classList.toggle(CLASS.active);
  };

  const close = () => {
    menu.classList.remove(CLASS.active);
    menuButton.classList.remove(CLASS.active);
  };

  const isOpen = () => {
    menu.classList.contains(CLASS.active);
  };

  menuButton.addEventListener("click", toggle);

  // 外部から制御できるようにオブジェクトを返す
  return { toggle, close, isOpen };
}

/**
 * ドロップダウンメニューの初期化
 */
function initDropdownMenu() {
  const dropdowns = document.querySelectorAll(SELECTOR.menuDropdown);

  if (dropdowns.length === 0) return;

  dropdowns.forEach((button) => {
    button.addEventListener("click", () => {
      button.classList.toggle(CLASS.active);
    });
  });
}

/**
 * スムーススクロールの初期化
 * @param {Object|null} hamburgerMenu - ハンバーガーメニューの制御オブジェクト
 */
function initSmoothScroll(hamburgerMenu) {
  const anchors = document.querySelectorAll(SELECTOR.anchorLink);

  if (anchors.length === 0) return;

  anchors.forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      const href = anchor.getAttribute("href");

      // 空のhrefや#のみの場合はデフォルト動作
      if (!href || href === "#") {
        e.preventDefault();
        scrollToPosition(0);
        return;
      }

      // #topの場合はページ上部へ
      if (href === "#top") {
        e.preventDefault();
        if (hamburgerMenu?.isOpen()) hamburgerMenu.close();
        scrollToPosition(0);
        return;
      }

      // 対象要素を取得
      const targetId = href.replace("#", "");
      const target = document.getElementById(targetId);

      if (!target) return; // 対象が存在しない場合はデフォルト動作

      e.preventDefault();

      // メニューが開いていれば閉じる
      if (hamburgerMenu?.isOpen()) hamburgerMenu.close();

      // スクロール位置を計算
      const headerHeight = getHeaderHeight();
      const position = window.scrollY + target.getBoundingClientRect().top - headerHeight;

      scrollToPosition(position);
    });
  });
}

/**
 * ページ遷移時のアンカー位置調整
 */
function initAnchorPositionAdjust() {
  window.addEventListener("load", () => {
    const url = location.href;

    if (!url.includes("#")) return;

    const hash = url.split("#").pop();
    if (!hash) return;

    const target = document.getElementById(hash);
    if (!target) return;

    const headerHeight = getHeaderHeight();
    const position = Math.floor(target.getBoundingClientRect().top + window.scrollY - headerHeight);

    scrollToPosition(position, "auto");
  });
}

/**
 * KV通過後にヘッダーへ is-blur を付与（トップページのみ・一方向）
 */
function initHeaderBlurOnKvPass() {
  const header = document.querySelector(SELECTOR.jsHeader);
  const kv = document.querySelector(SELECTOR.kv);

  if (!header || !kv) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      header.classList.toggle(CLASS.blur, !entry.isIntersecting);
    },
    {
      root: null,
      threshold: 0,
    },
  );

  observer.observe(kv);
}

/**
 * スクロールアニメーションの初期化（Intersection Observer）
 */
function initScrollAnimation() {
  const items = document.querySelectorAll(SELECTOR.scrollAnimation);

  if (items.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(CLASS.scrolled);
          // 一度アニメーションしたら監視を解除（パフォーマンス向上）
          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: "60% 0px -40% 0px",
      threshold: 0,
    },
  );

  items.forEach((item) => observer.observe(item));
}

// ========================================
// メイン初期化関数
// ========================================

/**
 * アプリケーション初期化
 */
export function initApp() {
  // ハンバーガーメニュー
  const hamburgerMenu = initHamburgerMenu();

  // ドロップダウンメニュー
  initDropdownMenu();

  // スムーススクロール（ハンバーガーメニューと連携）
  initSmoothScroll(hamburgerMenu);

  // ページ遷移時のアンカー位置調整
  initAnchorPositionAdjust();

  // スクロールアニメーション
  initScrollAnimation();

  // KV通過後にヘッダーへ is-blur を付与
  initHeaderBlurOnKvPass();

  // ページ読み込み完了後にis-showクラスを追加
  const loading = document.querySelector(SELECTOR.loading);
  if (loading) {
    window.addEventListener("load", () => {
      loading.classList.add(CLASS.show);
    });
  }

  // KVローディングアニメーション（セッション初回のみ）
  const homeKvLoading = document.querySelector(".js-home-kv-loading");
  if (homeKvLoading) {
    const SESSION_KEY = "kv-animation-shown";
    const hasShown = (() => {
      try {
        return sessionStorage.getItem(SESSION_KEY) === "1";
      } catch {
        // プライベートブラウジングなど sessionStorage が使えない環境はスキップ
        return true;
      }
    })();

    if (hasShown) {
      // 2回目以降はアニメーションをスキップして即座に表示完了状態にする
      homeKvLoading.classList.add("is-loading", "is-loaded", "is-inactive");
    } else {
      window.addEventListener("load", () => {
        setTimeout(() => {
          homeKvLoading.classList.add("is-loading");

          // KVウェーブパスの描画アニメーション
          const path = document.getElementById("drawPath");
          if (path) {
            const len = path.getTotalLength();
            const DRAW_DURATION = 1.4; // seconds

            const styleEl = document.createElement("style");
            styleEl.textContent = `
            @keyframes drawLine {
              from { stroke-dashoffset: ${-len}; }
              to   { stroke-dashoffset: 0; }
            }
          `;
            document.head.appendChild(styleEl);

            path.style.strokeDasharray = len;
            path.style.strokeDashoffset = 0;
            path.style.animation = `drawLine ${DRAW_DURATION}s linear forwards`;
          }

          setTimeout(() => {
            homeKvLoading.classList.add("is-loaded");
          }, 1300);
        }, 1800);

        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch {
          // sessionStorage への書き込みに失敗しても動作を継続する
        }
      });
    }
  }

  // ========================================
  // 追加の初期化処理はここに記述
  // ========================================

  // 例: 外部モジュールの読み込み
  // import("./modules/form.js").then(({ initForm }) => {
  //   initForm();
  // });
}
